import { z } from 'zod';
/**
 * Freigabe (Approval) — the second pair of eyes before an app does something
 * that leaves the house (a contract sent, an invoice issued). The app asks,
 * named reviewers decide, the app runs the step once the request is
 * approved. A request is never edited into a new stand: a new stand is a new
 * request, decisions are only appended (plan 053).
 */ export const ApprovalStatusSchema = z.enum([
    'pending',
    'approved',
    'rejected',
    'withdrawn',
    'executed',
    /** Run without the reviewers' word, in an emergency, with a reason (plan 053). */ 'overridden'
]);
export const ApprovalVerdictSchema = z.enum([
    'approved',
    'rejected'
]);
/** A member as an approval names them: id and the name shown on the card. */ export const ApprovalPersonSchema = z.object({
    id: z.string(),
    name: z.string()
});
/**
 * What the reviewer looks at, frozen when the app asks: key facts, a
 * document to open, and for a changed stand the changes. The platform shows
 * it as handed over; the app decides what belongs in it.
 */ export const ApprovalPreviewSchema = z.object({
    title: z.string().trim().min(1).max(200),
    facts: z.array(z.object({
        label: z.string().max(100),
        value: z.string().max(500)
    })).max(30).default([]),
    document: z.object({
        name: z.string().max(200),
        url: z.string().url().max(2000),
        mime: z.string().max(100).optional()
    }).optional(),
    changes: z.array(z.object({
        label: z.string().max(100),
        before: z.string().max(500),
        after: z.string().max(500)
    })).max(50).optional()
});
export const ApprovalDecisionSchema = z.object({
    by: ApprovalPersonSchema,
    at: z.string(),
    verdict: ApprovalVerdictSchema,
    /** Why: required for a rejection, the requester reads it. */ comment: z.string()
});
export const ApprovalOverrideSchema = z.object({
    by: ApprovalPersonSchema,
    at: z.string(),
    reason: z.string()
});
export const ApprovalSchema = z.object({
    _id: z.string(),
    appId: z.string(),
    /** The app's name for what is reviewed, e.g. `offer.send`. */ kind: z.string(),
    /** The app record the request is about, e.g. a deal id. */ ref: z.string(),
    /** The stand reviewed; a different snapshot needs a new request. */ snapshot: z.string(),
    label: z.string(),
    /** Where the record lives in the app, for "open in the app". */ url: z.string().optional(),
    preview: ApprovalPreviewSchema,
    requestedBy: ApprovalPersonSchema,
    requestedAt: z.string(),
    /** Who is asked; the requester is never among them. */ reviewers: z.array(ApprovalPersonSchema),
    requiredCount: z.number().int().min(0),
    dueAt: z.string().optional(),
    decisions: z.array(ApprovalDecisionSchema),
    status: ApprovalStatusSchema,
    override: ApprovalOverrideSchema.optional(),
    executedAt: z.string().optional(),
    createdAt: z.string(),
    updatedAt: z.string()
});
/**
 * Who must agree before a kind of step runs, per tenant, app and kind:
 * N of the named reviewers within some working days. Off means the step
 * runs at once.
 */ export const ApprovalRuleSchema = z.object({
    kind: z.string().trim().min(1).max(100),
    enabled: z.boolean().default(false),
    approvers: z.array(z.string()).max(50).default([]),
    requiredCount: z.number().int().min(1).max(50).default(1),
    dueWorkdays: z.number().int().min(1).max(30).default(2),
    /** Whether an admin may run the step without the reviewers (plan 053, emergency). */ allowOverride: z.boolean().default(true)
});

//# sourceMappingURL=approval.schema.js.map